<script lang="ts" setup>
import { Category } from '@/models/category.model';
import { ShiftCMSState } from '@/store';
import { CategoriesActions, CategoriesState } from '@/stores/categories.store';
import { ref, computed, onMounted, PropType, watch } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  label: {
    type: String,
  },
  modelValue: {
    type: Object as PropType<Category | undefined>,
    required: true,
  },
  required: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  main: {
    type: Boolean,
    default: () => false,
  },
  sub: {
    type: Object as PropType<Category>,
  },
});

const store = useStore<ShiftCMSState & { cs: CategoriesState }>();
const emit = defineEmits(['update:modelValue', 'change']);

const category = ref<Category | undefined>(props.modelValue);

function getParentString(c: Category, categories: Category[]): string {
  if (c.parentCategory) {
    const parent = categories.find((p) => p.id === c.parentCategory?.id);

    if (parent) {
      return `${getParentString(parent, categories)}${parent.title} - `;
    }
  }
  return '';
}

const parents = computed(() => [
  ...store.state.cs.categories.filter((v) =>
    props.sub ? v.parentCategory?.id === props.sub.id : false,
  ),
]);

const categoryItems = computed(() =>
  [...store.state.cs.categories]
    .sort((a, b) => a.index - b.index)
    .filter((v) => (props.main ? !v.parentCategory : true))
    .filter((v) =>
      props.sub
        ? (v.parentCategory &&
            [...parents.value.map((v) => v.id)].includes(
              v.parentCategory?.id,
            )) ||
          v.parentCategory?.id === props.sub.id
        : true,
    )
    .map((c) => {
      return {
        title: `${
          getParentString(c, store.state.cs.categories)
            ? `${getParentString(c, store.state.cs.categories)}`
            : ''
        }${c.title}`,
        value: c,
      };
    }),
);

function update(v: Category) {
  emit('update:modelValue', v);
  emit('change');
}

watch(
  () => props.modelValue,
  () => {
    category.value = props.modelValue;
  },
  { deep: true },
);

onMounted(() => {
  store.dispatch(CategoriesActions.GetCategories);
});
</script>
<template>
  <v-autocomplete
    :label="props.label ? props.label : 'Category'"
    v-model="category"
    :items="categoryItems"
    chips
    :disabled="disabled"
    :multiple="false"
    :valueComparator="(a, b) => a.id === b.id"
    @update:modelValue="update"
    ><template v-slot:item="{ props }">
      <v-list-item v-bind="props">
        <v-list-item-title></v-list-item-title>
      </v-list-item>
    </template>
  </v-autocomplete>
  <v-container style="padding-top: 0; padding-bottom: 0">
    <v-alert type="warning" density="compact" v-if="!category && required"
      >This is required</v-alert
    >
  </v-container>
</template>
